
import { useState, useEffect } from 'react';
import { Business } from '@/data/types/businessTypes';
import { useToast } from '@/hooks/use-toast';
import { businessUtils } from '@/context/utils/businessUtils'; // Replaced useDataUtils

export type LogoLayout = 'square' | 'landscape' | 'portrait';

export const useLogoUpload = (business: Business, isEditMode: boolean = false) => {
  const { toast } = useToast();
  
  // Logo file state
  const [file, setFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(business?.logoUrl || null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  
  // Preview tab state
  const [previewTab, setPreviewTab] = useState<string>('google-ad');
  
  // Logo layout option - convert 'full' to 'square' for backwards compatibility
  const [logoLayout, setLogoLayout] = useState<LogoLayout>(
    business?.logoLayout === 'full' ? 'square' : (business?.logoLayout as LogoLayout || 'square')
  );
  
  // Update logo preview when business changes
  useEffect(() => {
    if (business?.logoUrl) {
      setLogoPreview(business.logoUrl);
      console.log('Logo preview updated from business', business.logoUrl);
    }
    
    // Convert 'full' layout to 'square' for backwards compatibility
    if (business?.logoLayout) {
      const layout = business.logoLayout === 'full' ? 'square' : business.logoLayout as LogoLayout;
      setLogoLayout(layout);
      console.log('Logo layout updated from business', layout);
    }
  }, [business?.logoUrl, business?.logoLayout]);
  
  // Handle file change
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    setUploadError(null);
    
    if (selectedFile) {
      // Validate file type
      if (!selectedFile.type.startsWith('image/')) {
        setUploadError('Please upload an image file');
        return;
      }
      
      // Validate file size (max 5MB)
      if (selectedFile.size > 5 * 1024 * 1024) {
        setUploadError('Image size must be less than 5MB');
        return;
      }
      
      setFile(selectedFile);
      
      // Create a preview URL
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target?.result as string);
        console.log('Logo preview updated from file upload');
      };
      reader.readAsDataURL(selectedFile);
    }
  };
  
  // Handle save logo
  const handleSaveLogo = () => {
    if (!business.id) {
      setUploadError('Business ID is missing');
      return false;
    }
    
    if (!logoPreview) {
      setUploadError('No logo to save');
      return false;
    }
    
    try {
      // Update business with logo URL and layout
      businessUtils.updateBusiness(business.id, {
        logoUrl: logoPreview,
        logoLayout: logoLayout
      });
      
      // Reset file selection
      setFile(null);
      
      // Show success toast
      toast({
        title: 'Logo saved',
        description: 'Your business logo has been updated successfully.',
      });
      
      return true;
    } catch (error) {
      console.error('Error saving logo:', error);
      setUploadError('Failed to save logo');
      return false;
    }
  };
  
  // Handle reset
  const handleReset = () => {
    setFile(null);
    setLogoPreview(business?.logoUrl || null);
    setUploadError(null);
    console.log('Logo reset to', business?.logoUrl);
  };
  
  // Handle remove logo
  const handleRemoveLogo = () => {
    setFile(null);
    setLogoPreview(null);
    
    if (business.id) {
      businessUtils.updateBusiness(business.id, {
        logoUrl: null,
        logoLayout: 'square'  // Default to square when removing logo
      });
      console.log('Logo removed');
    }
  };
  
  // Create a wrapper function for setPreviewTab that accepts a string
  const handleSetPreviewTab = (tab: string) => {
    setPreviewTab(tab);
  };
  
  return {
    logoPreview,
    file,
    uploadError,
    previewTab,
    setPreviewTab: handleSetPreviewTab,
    logoLayout,
    setLogoLayout,
    handleFileChange,
    handleSaveLogo,
    handleReset,
    handleRemoveLogo
  };
};
